// client/src/components/Auth/Login.js
import React, { useState } from "react";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import useColorMode from "../../hooks/useColorMode";
import { useNavigate, useLocation } from "react-router-dom";
import { puedeAcceder } from "../../routes/permisosRuta";
import styles from "./Login.module.css";

function Login() {
  const [formData, setFormData] = useState({
    correo: "",
    contraseña: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const { mode, toggleMode } = useColorMode();
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(""); // Limpiar error al escribir
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.correo || !formData.contraseña) {
      setError("Por favor completa todos los campos");
      setLoading(false);
      return;
    }

    const result = await login(formData.correo, formData.contraseña);

    if (result.success) {
      /* Si ProtectedRoute nos redirigió aquí con state.from (típico cuando la
       * sesión expiró en medio de una URL protegida), volvemos ahí — pero SOLO
       * si el rol puede abrir esa ruta. Antes se respetaba a ciegas: un rol sin
       * viajes.leer (p.ej. Monitoreo) que hubiera caducado en /dashboard volvía
       * a /dashboard y rebotaba a /unauthorized en CADA ingreso.
       *
       * Cuando no se puede volver, vamos a "/" y SmartRedirect decide.
       *
       * Los permisos se leen de result.user y no de hasPermission: este handler
       * se creó en el render ANTERIOR al login, cuando user era null, así que
       * el hasPermission capturado aquí siempre respondería false. */
      const permisos = result.user?.permisos ?? [];
      const from = location.state?.from?.pathname;
      const destino =
        from && puedeAcceder(from, (permiso) => permisos.includes(permiso))
          ? from
          : "/";

      navigate(destino, { replace: true });
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className={styles.loginContainer}>
      <Tooltip title={mode === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}>
        <IconButton
          onClick={toggleMode}
          className={styles.themeToggle}
          aria-label="Cambiar modo de color"
          size="medium"
        >
          {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Tooltip>

      <div className={styles.loginBackground}>
        <div className={styles.loginCard}>
          <Paper elevation={8} className={styles.loginPaper}>
            <Box className={styles.loginHeader}>
              <div className={styles.logoContainer}>
                <LoginIcon className={styles.logoIcon} />
              </div>
              <Typography variant="h4" className={styles.loginTitle}>
                SysGago
              </Typography>
              <Typography variant="subtitle1" className={styles.loginSubtitle}>
                Sistema de Gestión de Transporte
              </Typography>
            </Box>

            <form onSubmit={handleSubmit} className={styles.loginForm}>
              {error && (
                <Alert
                  severity="error"
                  className={styles.errorAlert}
                  sx={{
                    backgroundColor: "var(--color-danger-soft-bg)",
                    color: "var(--color-danger-soft-fg)",
                    border: "1px solid var(--color-danger-soft-bd)",
                    "& .MuiAlert-icon": {
                      color: "var(--color-danger)",
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                name="correo"
                type="email"
                label="Correo electrónico"
                placeholder="ejemplo@transgago.com"
                autoComplete="email"
                value={formData.correo}
                onChange={handleChange}
                variant="outlined"
                className={styles.textField}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "var(--color-bg-soft-2)",
                    borderRadius: "10px",
                    color: "var(--color-text-title) !important",
                    "& fieldset": {
                      borderColor: "var(--color-border)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--color-border-strong)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "var(--color-bg-card)",
                      "& fieldset": {
                        borderColor: "var(--color-primary)",
                      },
                    },
                    "& input": {
                      color: "var(--color-text-title) !important",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "var(--color-text-muted) !important",
                    "&.Mui-focused": {
                      color: "var(--color-primary) !important",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "var(--color-text-muted)" }} />
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
              />

              <TextField
                fullWidth
                name="contraseña"
                type={showPassword ? "text" : "password"}
                label="Contraseña"
                autoComplete="current-password"
                value={formData.contraseña}
                onChange={handleChange}
                variant="outlined"
                className={styles.textField}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "var(--color-bg-soft-2)",
                    borderRadius: "10px",
                    color: "var(--color-text-title) !important",
                    "& fieldset": {
                      borderColor: "var(--color-border)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--color-border-strong)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "var(--color-bg-card)",
                      "& fieldset": {
                        borderColor: "var(--color-primary)",
                      },
                    },
                    "& input": {
                      color: "var(--color-text-title) !important",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "var(--color-text-muted) !important",
                    "&.Mui-focused": {
                      color: "var(--color-primary) !important",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: "var(--color-text-muted)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "var(--color-text-muted)" }}
                        aria-label="mostrar u ocultar contraseña"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                className={styles.loginButton}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />
                }
              >
                {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
              </Button>
            </form>

            <Box className={styles.loginFooter}>
              <Typography variant="caption" className={styles.footerText}>
                © 2024 TransGago. Todos los derechos reservados.
              </Typography>
            </Box>
          </Paper>
        </div>
      </div>
    </div>
  );
}

export default Login;
